import supabase from '@/supabase'
import type { Company, CompanyRead, CompositeCompany, Connection, ConnectionRequest, CreateCompanyPayload, UpdateCompany } from '@/types/supabase'

type RawCompositeCompany = Company & {
  user_count: { count: number }[]
  car_count: { count: number }[]
  companies_read: Pick<CompanyRead, 'company_id'>[]
  connections_to: Pick<Connection, 'id' | 'created_at'>[]
  connections_from: Pick<Connection, 'id' | 'created_at'>[]
  connection_requests_to: Pick<ConnectionRequest, 'id' | 'from_company_id' | 'created_at' | 'message'>[]
  connection_requests_from: Pick<ConnectionRequest, 'id' | 'to_company_id' | 'created_at' | 'message'>[]
}

class CompaniesApi {

  public async fetchCompanies(page: number, limit: number | null, sortBy: SortBy<Company>, currentCompanyId: string | null): Promise<CompositeCompany[]> {
    let query = supabase
      .from('companies')
      .select(`
        *,
        user_count:users!users_company_id_fkey(count),
        car_count:cars!cars_company_id_fkey(count),
        companies_read(company_id),
        connections_to:connections!connections_to_company_id_fkey(id, created_at),
        connections_from:connections!connections_from_company_id_fkey(id, created_at),
        connection_requests_to:connection_requests!connection_requests_to_company_id_fkey(id, from_company_id, created_at, message),
        connection_requests_from:connection_requests!connection_requests_from_company_id_fkey(id, to_company_id, created_at, message)
      `)
    for (const { key, ascending } of sortBy.keys) {
      query = query.order(key, { ascending })
    }
    if (currentCompanyId !== null) {
      query = query.neq('id', currentCompanyId)
    }
    if (limit !== null) {
      query.range((page - 1) * limit, page * limit - 1)
    }
    const { data, error } = await query
    if (error) throw error
    return data.map((company) => this.toCompositeCompany(company, currentCompanyId))
  }

  public async fetchCompaniesCount(currentCompanyId: string | null): Promise<number> {
    let query = supabase
      .from('companies')
      .select('*', { count: 'exact', head: true })
    if (currentCompanyId !== null) {
      query = query.neq('id', currentCompanyId)
    }
    const { count, error } = await query
    if (error) throw error
    return count ?? 0
  }

  public async fetchAllCompanyNames(): Promise<Pick<Company, 'id' | 'company_name'>[]> {
    const { data, error } = await supabase
      .from('companies')
      .select('id, company_name')
      .order('company_name', { ascending: true })
    if (error) throw error
    return data
  }

  public async fetchCompanyNameByIds(ids: string[]): Promise<Map<Company['id'], Company['company_name']>> {
    const { data, error } = await supabase
      .from('companies')
      .select('id, company_name')
      .in('id', ids)
    if (error) throw error
    return data.reduce((acc, company) => {
      acc.set(company.id, company.company_name)
      return acc
    }, new Map())
  }

  public async fetchCompany(id: string, currentCompanyId: string | null): Promise<CompositeCompany> {
    const { data, error } = await supabase
      .from('companies')
      .select(`
        *,
        user_count:users!users_company_id_fkey(count),
        car_count:cars!cars_company_id_fkey(count),
        companies_read(company_id),
        connections_to:connections!connections_to_company_id_fkey(id, created_at),
        connections_from:connections!connections_from_company_id_fkey(id, created_at),
        connection_requests_to:connection_requests!connection_requests_to_company_id_fkey(id, from_company_id, created_at, message),
        connection_requests_from:connection_requests!connection_requests_from_company_id_fkey(id, to_company_id, created_at, message)
      `)
      .eq('id', id)
      .single()
    if (error) throw error
    return this.toCompositeCompany(data, currentCompanyId)
  }

  public async fetchCompanyName(id: string): Promise<string> {
    const { data, error } = await supabase
      .from('companies')
      .select('company_name')
      .eq('id', id)
      .single()
    if (error) throw error
    return data.company_name
  }

  public async createCompany(payload: CreateCompanyPayload): Promise<Company> {
    const { data, error } = await supabase
      .from('companies')
      .insert(payload)
      .select()
      .single()
    if (error) throw error
    return data
  }

  public async updateCompany(id: string, payload: UpdateCompany): Promise<Company> {
    const { data, error } = await supabase
      .from('companies')
      .update(payload)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  }

  public async deleteCompany(id: string): Promise<void> {
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('id', id)
    if (error) throw error
  }

  private toCompositeCompany = (company: RawCompositeCompany, currentCompanyId: string | null): CompositeCompany => {
    // Determine connection status
    const connectionTo = company.connections_to[0]
    const connectionFrom = company.connections_from[0]
    const connection = connectionTo
      ? { id: connectionTo.id, created_at: connectionTo.created_at }
      : connectionFrom
        ? { id: connectionFrom.id, created_at: connectionFrom.created_at }
        : null

    // Determine connection request status
    const incomingRequest = company.connection_requests_from.find(r => r.to_company_id === currentCompanyId)
    const outgoingRequest = company.connection_requests_to.find(r => r.from_company_id === currentCompanyId)
    const connection_request = incomingRequest
      ? { id: incomingRequest.id, direction: 'incoming' as const, created_at: incomingRequest.created_at, message: incomingRequest.message }
      : outgoingRequest
        ? { id: outgoingRequest.id, direction: 'outgoing' as const, created_at: outgoingRequest.created_at, message: outgoingRequest.message }
        : null

    return {
      ...company,
      user_count: company.user_count.reduce((acc, c) => acc + (c.count ?? 0), 0),
      car_count: company.car_count.reduce((acc, c) => acc + (c.count ?? 0), 0),
      is_unread: company.companies_read.length === 0,
      connection,
      connection_request
    }
  }
}

export default new CompaniesApi()
